import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Tooltip
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import CancelPresentationRoundedIcon from '@mui/icons-material/CancelPresentationRounded';
import ContactPhoneRoundedIcon from '@mui/icons-material/ContactPhoneRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Alerta, PopupDialog } from '../../components/alerta';
import '../css/crmDashboard.css';
import '../css/crmClientTabs.css';
import '../css/crmLeadDetails.css';
import { fetchFlask } from '../../utils/fetchFlask';
import DadosUsuario from '../../utils/context';
import { formatDateTimeTextPtBr } from '../../utils/dateUtils';
import { getPhoneDigits, isValidPhone, maskCpfCnpj, maskPhone } from '../../utils/maskCpfCnpj';
import { formatPercentRatio, parseNumericText } from '../../utils/numberUtils';
import {
    CNPJ_DIGITS_LENGTH,
    formatCurrency,
    formatSnakeCaseLabel,
    getDigits
} from '../../utils/stringFormat';
import { isPostSaleProfile } from '../../utils/userProfile';
import {
    FASE_OPTIONS,
    ORIGEM_OPTIONS,
    STATUS_OPTIONS,
    VENDEDOR_OPTIONS,
    buildSignatureReminderText,
    buildWhatsAppUrl,
    formatDaysAgo,
    getDaysSince,
    getLeadPhaseToneClass,
    getLeadStatusToneClass,
    getOptionIdByLabel
} from './crmShared';
import {
    CRM_CLIENT_EMPTY_CONTACTS,
    CRM_CLIENT_EMPTY_FINANCE,
    CRM_CLIENT_TABS,
    FINANCE_PAGE_SIZE,
    fetchClientContacts,
    fetchClientFinance,
    fetchClientProtocols,
    fetchClientUnits,
    cancelReceivable,
    copyPortalUser,
    deactivateClientUnit,
    distractClient,
    extendReceivable,
    getContactUnitOptions,
    getDistractDocuments,
    getDistractUnitNames,
    hasContactUpdateValues,
    sendEmailVerificationCode,
    updateClientContact,
    updatePortalEmail,
    updatePortalPassword
} from './crmClientData';
import {
    ClientContactForm,
    ClientDistractDialog,
    ClientContactsPanel,
    ClientFinancePanel,
    ClientPanelState,
    ClientPortalLoginForm,
    ClientProtocolsPanel,
    ClientUnitsPanel,
    Field,
    Pill,
    SummaryTile
} from './crmClientTabs';
import { fetchCrmLeadById } from './crmLeadsData';

const CRM_LEAD_UPDATE_API = 'crm/updateLeadData';
const PROPOSTA_DIGITAL_API = 'propostaDigital';
const AI_AGENT_SELLER_LABEL = 'Agente IA';
const AI_AGENT_SELLER_OPTION = VENDEDOR_OPTIONS.find((option) => option.label === AI_AGENT_SELLER_LABEL);
const OBS_MAX_LENGTH = 1024;
const DESCONTO_MIN = 0;
const DESCONTO_MAX = 1;
const CRM_LEAD_UNITS_API = 'crm/leadsUnits';
const CRM_LEAD_UNITS_UPDATE_API = 'crm/updateLeadUnits';
const CRM_LEAD_UNITS_INSERT_API = 'crm/insertLeadUnits';
const CRM_LEAD_UNITS_DELETE_API = 'crm/deleteUnit';
const CRM_LEAD_SERASA_API = 'checkSerasa';
const DIA_VENCIMENTO_OPTIONS = [10, 18, 28];
const COMPLETED_STATUS = STATUS_OPTIONS.find((option) => option.id === 8);
const CONTRACT_PHASE = FASE_OPTIONS.find((option) => option.id === 4);
const WON_PHASE = FASE_OPTIONS.find((option) => option.id === 5);
const AUTOMATED_PHASE_IDS = new Set([CONTRACT_PHASE.id, WON_PHASE.id]);

const TAB_ICONS = {
    lead: PersonRoundedIcon,
    unidades: HomeWorkRoundedIcon,
    contato: ContactPhoneRoundedIcon,
    protocolos: DescriptionRoundedIcon,
    financeiro: ReceiptLongRoundedIcon
};

const CLIENT_ONLY_TAB_KEYS = new Set(['contato', 'protocolos', 'financeiro']);

const selectMenuProps = {
    PaperProps: { className: 'crm-dashboard-select-menu-paper' },
    MenuListProps: { className: 'crm-dashboard-select-menu-list' }
};

const readOnlyInputProps = {
    readOnly: true,
    endAdornment: (
        <InputAdornment position='end' className='crm-lead-details-readonly-adornment'>
            <LockRoundedIcon fontSize='inherit' />
        </InputAdornment>
    )
};

const parseDiscount = (value) => parseNumericText(value, null);

const buildPayload = (formData) => {
    const payload = {
        id: Number(formData.lead_id),
        indicacao_id: Number(formData.indicacao_id),
        telefone: formData.telefone,
        nome: formData.nome,
        email: formData.email,
        cpf: formData.cpf,
        ticket: formData.ticket,
        vendedor_id: getOptionIdByLabel(VENDEDOR_OPTIONS, formData.vendedor),
        status_id: getOptionIdByLabel(STATUS_OPTIONS, formData.status),
        fase_id: getOptionIdByLabel(FASE_OPTIONS, formData.fase),
        origem_id: getOptionIdByLabel(ORIGEM_OPTIONS, formData.origem),
        dia_vencimento: formData.dia_vencimento,
        cupom: formData.cupom,
        obs: formData.obs ?? ''
    };

    const desconto = parseDiscount(formData.desconto);
    if (desconto !== null) {
        payload.desconto = desconto;
    }

    return payload;
};

const selectFields = [
    { field: 'vendedor', label: 'Vendedor', kind: 'select', span: 'is-medium', options: VENDEDOR_OPTIONS },
    { field: 'status', label: 'Status', kind: 'select', span: 'is-medium', options: STATUS_OPTIONS },
    { field: 'fase', label: 'Fase', kind: 'select', span: 'is-medium', options: FASE_OPTIONS },
    { field: 'origem', label: 'Origem', kind: 'select', span: 'is-medium', options: ORIGEM_OPTIONS }
];

const dueDayField = { field: 'dia_vencimento', label: 'Dia de vencimento', kind: 'dueDay', span: 'is-small' };
const obsField = { field: 'obs', label: 'Observação', kind: 'obs', span: 'is-full' };

const identityFields = [
    { field: 'nome', label: 'Nome', span: 'is-large' },
    { field: 'cpf', label: 'CPF', span: 'is-small' },
    { field: 'telefone', label: 'Telefone', readOnly: true, span: 'is-medium' },
    { field: 'email', label: 'Email', span: 'is-medium' }
];

const commercialTextFields = [
    { field: 'ticket', label: 'Ticket', span: 'is-small' },
    { field: 'desconto', label: 'Desconto', span: 'is-small', supervisionOnly: true }
];

const referralFields = [
    { field: 'indicacao_id', label: 'Indicação ID', type: 'number', span: 'is-medium' },
    { field: 'cupom', label: 'Cupom', span: 'is-medium' }
];

const DOCUMENT_REQUIRED_FIELDS = [
    { field: 'telefone', label: 'telefone' },
    { field: 'nome', label: 'nome' },
    { field: 'email', label: 'email' },
    { field: 'cpf', label: 'CPF' },
    { field: 'ticket', label: 'ticket' },
    { field: 'vendedor', label: 'vendedor' },
    { field: 'origem', label: 'origem' }
];

const LEAD_FIELD_CONFIGS = [
    ...identityFields,
    ...selectFields,
    ...commercialTextFields,
    ...referralFields,
    dueDayField,
    obsField
];

const DOCUMENT_REQUIRED_FIELD_NAMES = new Set(DOCUMENT_REQUIRED_FIELDS.map(({ field }) => field));

const canEditSeller = (seller) => !seller || seller === AI_AGENT_SELLER_LABEL;
const matchesLeadOption = (lead, field, idField, option) => (
    lead?.[field] === option.label || Number(lead?.[idField]) === option.id
);

const getLeadFieldDisplayValue = (field, value, focusedField) => {
    if (field === 'cpf') return maskCpfCnpj(value);
    if (field === 'cadastro') return formatDateTimeTextPtBr(value);
    if (field === 'ticket' && focusedField !== 'ticket') return formatCurrency(value);
    if (field === 'desconto' && focusedField !== 'desconto') {
        return value === null || value === undefined || value === '' ? '' : formatPercentRatio(value);
    }
    return value ?? '';
};

const unitFieldOrder = [
    'titular',
    'codigo_do_cliente',
    'endereco',
    'instalacao',
    'consumo_medio',
    'crefaz',
    'baixa_renda',
    'alta_tensao',
    'importe',
    'periodo',
    'confianca'
];

const UNIT_FIELD_LABELS = {
    titular: 'Titular',
    codigo_do_cliente: 'Código do cliente',
    endereco: 'Endereço',
    instalacao: 'Instalação',
    consumo_medio: 'Consumo médio',
    crefaz: 'Crefaz',
    baixa_renda: 'Baixa renda',
    alta_tensao: 'Alta tensão',
    importe: 'Importe',
    periodo: 'Período',
    confianca: 'Confiança',
    rua: 'Rua',
    logradouro: 'Logradouro',
    numero: 'Número',
    complemento: 'Complemento',
    bairro: 'Bairro',
    cidade: 'Cidade',
    estado: 'Estado',
    uf: 'UF',
    cep: 'CEP'
};

const UNIT_FLAGS = [
    { field: 'alta_tensao', label: 'Alta tensão', tone: 'is-info' },
    { field: 'baixa_renda', label: 'Baixa renda', tone: 'is-warning' },
    { field: 'crefaz', label: 'Crefaz', tone: 'is-danger' }
];

const hiddenUnitFields = new Set(['unidade_id', 'lead_id']);
const editableUnitFields = new Set(['titular', 'codigo_do_cliente', 'instalacao', 'consumo_medio']);
const editableAddressFields = new Set(['bairro', 'cep', 'cidade', 'complemento', 'estado', 'logradouro', 'numero', 'rua', 'uf']);
const isEditableUnitField = (field) => editableUnitFields.has(field);
const isEditableAddressField = (field) => editableAddressFields.has(field);
const editableUnitFieldList = ['titular', 'codigo_do_cliente', 'instalacao', 'consumo_medio'];
const editableAddressFieldList = ['rua', 'bairro', 'numero', 'cidade', 'complemento', 'estado', 'cep'];
const requiredManualUnitFieldList = ['titular', 'codigo_do_cliente', 'instalacao', 'consumo_medio'];
const requiredManualAddressFieldList = ['bairro', 'cep', 'cidade', 'estado', 'numero', 'rua'];
const disabledInsertUnitFieldList = unitFieldOrder.filter((field) => field !== 'endereco' && field !== 'alta_tensao' && !editableUnitFields.has(field));

const getUnitFieldLabel = (field) => UNIT_FIELD_LABELS[field] ?? formatSnakeCaseLabel(field);

const CONSUMO_MEDIO_PATTERN = /^\d+([.,]\d+)?$/;
const INVALID_CONSUMO_MEDIO_MESSAGE = 'Consumo médio inválido. Informe apenas números, ex.: 1500 ou 1500,50.';
const isValidConsumoMedio = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'number') return Number.isFinite(value);

    return CONSUMO_MEDIO_PATTERN.test(value.toString().trim());
};

// A API omite campos nulos; sem isso, unidade sem consumo/instalação não teria onde preenchê-los.
const withEditableUnitFields = (unit) => ({
    ...editableUnitFieldList.reduce((acc, field) => ({ ...acc, [field]: '' }), {}),
    ...unit,
    endereco: {
        ...editableAddressFieldList.reduce((acc, field) => ({ ...acc, [field]: '' }), {}),
        ...(unit?.endereco ?? {})
    }
});

const createManualUnitForm = () => ({
    titular: '',
    codigo_do_cliente: '',
    instalacao: '',
    consumo_medio: '',
    alta_tensao: false,
    endereco: editableAddressFieldList.reduce((acc, field) => ({ ...acc, [field]: '' }), {})
});

const buildInsertUnitPayload = (manualUnitForm) => {
    const payload = {
        titular: manualUnitForm.titular?.trim() ?? '',
        codigo_do_cliente: manualUnitForm.codigo_do_cliente?.trim() ?? '',
        instalacao: manualUnitForm.instalacao?.trim() ?? '',
        consumo_medio: manualUnitForm.consumo_medio?.trim() ?? '',
        alta_tensao: Boolean(manualUnitForm.alta_tensao),
        endereco: editableAddressFieldList.reduce((acc, field) => {
            acc[field] = manualUnitForm.endereco?.[field]?.trim() ?? '';
            return acc;
        }, {})
    };

    disabledInsertUnitFieldList.forEach((field) => {
        payload[field] = false;
    });

    return payload;
};

const getCpfCnpjLabel = (value) => {
    const digits = getDigits(value);

    if (!digits) return 'CPF ou CNPJ';
    if (digits.length === CNPJ_DIGITS_LENGTH) return 'CNPJ';
    return 'CPF';
};

const hasDocumentFieldValue = (value) => value !== null && value !== undefined && value.toString().trim() !== '';

const formatUnitValue = (value, parentKey = '') => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    if (typeof value === 'boolean') {
        return value ? 'Sim' : 'Não';
    }

    if (Array.isArray(value)) {
        return value.map((item) => formatUnitValue(item, parentKey)).join(', ');
    }

    if (typeof value === 'object') {
        return Object.entries(value)
            .filter(([, nestedValue]) => nestedValue !== null && nestedValue !== undefined && nestedValue !== '')
            .map(([key, nestedValue]) => `${getUnitFieldLabel(key)}: ${formatUnitValue(nestedValue, key)}`)
            .join('\n');
    }

    if (parentKey === 'consumo_medio' && typeof value === 'number') {
        return String(value);
    }

    return String(value);
};

const getUnitEntries = (unit) => {
    const orderedEntries = unitFieldOrder
        .filter((field) => field in unit)
        .map((field) => [field, unit[field]]);

    const remainingEntries = Object.entries(unit).filter(([field]) => !unitFieldOrder.includes(field) && !hiddenUnitFields.has(field));

    return [...orderedEntries, ...remainingEntries];
};

const getAddressEntries = (address = {}) => Object.entries(address).sort(([fieldA], [fieldB]) => fieldA.localeCompare(fieldB));
const areUnitsEqual = (unitA, unitB) => JSON.stringify(unitA) === JSON.stringify(unitB);
const formatUnitPeriodForApi = (periodo) => {
    if (typeof periodo !== 'string' || periodo.trim() === '') {
        return periodo;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(periodo)) {
        return periodo;
    }

    const match = periodo.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) {
        return periodo;
    }

    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
};

const buildUnitPayload = (unit) => ({
    ...unit,
    periodo: formatUnitPeriodForApi(unit?.periodo),
    endereco: unit?.endereco ? { ...unit.endereco } : unit?.endereco
});

const getUnitStreetLabel = (address) => {
    const safeAddress = address ?? {};
    const street = safeAddress.rua ?? safeAddress.logradouro ?? '';
    const streetLine = [street, safeAddress.numero].filter(hasDocumentFieldValue).join(', ');

    return streetLine || 'Endereço não informado';
};

const buildUnitAddressLabel = (address) => {
    const safeAddress = address ?? {};
    const street = safeAddress.rua ?? safeAddress.logradouro ?? '';
    const streetLine = [street, safeAddress.numero].filter(hasDocumentFieldValue).join(', ');
    const cityLine = [safeAddress.cidade, safeAddress.estado ?? safeAddress.uf].filter(hasDocumentFieldValue).join('/');
    const cep = hasDocumentFieldValue(safeAddress.cep) ? `CEP ${safeAddress.cep}` : '';

    return [streetLine, safeAddress.complemento, safeAddress.bairro, cityLine, cep]
        .filter(hasDocumentFieldValue)
        .join(' · ');
};

const extractLeadUnitsFromInsertResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.units)) return response.units;
    if (response?.data && typeof response.data === 'object') return [response.data];
    return [];
};

const mergeLeadUnitsAfterInsert = (currentUnits, returnedUnits) => {
    if (!Array.isArray(returnedUnits) || returnedUnits.length === 0) return currentUnits;
    if (!Array.isArray(currentUnits) || currentUnits.length === 0) return returnedUnits;

    const serialize = (unit) => JSON.stringify(unit);
    const currentSerialized = new Set(currentUnits.map(serialize));
    const returnedSerialized = new Set(returnedUnits.map(serialize));

    const returnedContainsAllCurrent = currentUnits.every((unit) => returnedSerialized.has(serialize(unit)));
    if (returnedContainsAllCurrent) {
        return returnedUnits;
    }

    const newUnitsOnly = returnedUnits.filter((unit) => !currentSerialized.has(serialize(unit)));
    if (newUnitsOnly.length === 0) {
        return currentUnits;
    }

    return [...currentUnits, ...newUnitsOnly];
};

const joinLabelsPtBr = (labels) => {
    if (labels.length <= 1) return labels.join('');

    return `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`;
};

// Unidades vindas da API podem trazer logradouro/uf no lugar de rua/estado.
const UNIT_ADDRESS_FIELD_ALIASES = { rua: ['rua', 'logradouro'], estado: ['estado', 'uf'] };
const getMissingUnitAddressFields = (address) => requiredManualAddressFieldList.filter((field) => (
    !(UNIT_ADDRESS_FIELD_ALIASES[field] ?? [field]).some((alias) => hasDocumentFieldValue(address?.[alias]))
));

const countChangedFields = (leadData, initialLeadData) => {
    if (!leadData || !initialLeadData) return 0;

    return Object.keys({ ...initialLeadData, ...leadData })
        .filter((field) => JSON.stringify(leadData[field]) !== JSON.stringify(initialLeadData[field]))
        .length;
};

const LeadSection = ({ icon: Icon, title, count, copy, action, children }) => (
    <section className='crm-client-block crm-lead-details-block'>
        <div className='crm-lead-details-block-head'>
            <h3 className='crm-client-block-title'>
                <Icon fontSize='inherit' />
                {title}
                {count !== undefined && <span className='crm-client-block-count'>{count}</span>}
            </h3>
            {action}
        </div>
        {copy && <p className='crm-lead-details-section-copy'>{copy}</p>}
        {children}
    </section>
);

const ActionHint = ({ children }) => (
    children ? <p className='crm-lead-details-action-hint'>{children}</p> : null
);

const BR_COUNTRY_CODE = '55';

const buildPhoneUrl = (phone) => `tel:+${BR_COUNTRY_CODE}${getPhoneDigits(phone)}`;

const SELECT_ACCIDENTAL_PICK_MS = 400;

const LOST_STATUS_LABELS = new Set(['Cancelado', 'Sem Interesse']);
const isLostLeadStatus = (status) => (
    typeof status === 'string' && (status.startsWith('Vetado') || LOST_STATUS_LABELS.has(status))
);

const buildLeadUrl = (leadId) => `${window.location.origin}/CRM/leads/${leadId}`;

const HeaderContactAction = ({ label, disabledReason, href, onClick, icon: Icon, external = false }) => {
    const isDisabled = Boolean(disabledReason);
    const tooltip = disabledReason || label;

    return (
        <Tooltip title={tooltip} arrow>
            <span className='crm-lead-details-contact-slot'>
                {href && !isDisabled
                    ? (
                        <a
                            className='crm-lead-details-contact-action'
                            href={href}
                            aria-label={label}
                            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                            <Icon fontSize='inherit' />
                        </a>
                        )
                    : (
                        <button
                            type='button'
                            className='crm-lead-details-contact-action'
                            aria-label={label}
                            onClick={onClick}
                            disabled={isDisabled}
                        >
                            <Icon fontSize='inherit' />
                        </button>
                        )}
            </span>
        </Tooltip>
    );
};

const CrmLeadDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { leadId } = useParams();
    const queryClient = useQueryClient();
    const dadosUsuario = useContext(DadosUsuario);
    const emailPortal = dadosUsuario.email === 'maurofarias@settaenergia.com.br';
    const isSupervision = emailPortal || dadosUsuario.autorizacoes?.includes('Adm');
    const canOverrideSellerLock = isSupervision;
    const canViewLeadUnits = !isPostSaleProfile(dadosUsuario.autorizacoes);
    const canManageDistract = ['Relacionamento', 'Adm'].some((role) => dadosUsuario.autorizacoes?.includes(role));
    const canSkipAgentContractForm = dadosUsuario.autorizacoes?.includes('MKT');
    const routeLeadData = location.state?.leadData;
    const hasRouteLead = Boolean(routeLeadData) && String(routeLeadData.lead_id) === String(leadId);
    const leadListState = location.state?.leadListState;
    const [leadNotice] = useState(() => location.state?.leadNotice ?? null);
    const [initialTab] = useState(() => location.state?.initialTab ?? 'lead');
    const navigateBackToLeads = () => navigate('/CRM/leads', { state: { leadListState } });

    const [leadData, setLeadData] = useState(() => (hasRouteLead ? { ...routeLeadData } : null));
    const [initialLeadData, setInitialLeadData] = useState(() => (hasRouteLead ? { ...routeLeadData } : null));
    const [leadLoadState, setLeadLoadState] = useState(hasRouteLead ? 'ready' : 'loading');
    const [leadReloadKey, setLeadReloadKey] = useState(0);
    const loadedLeadIdRef = useRef(hasRouteLead ? String(leadId) : null);
    const [message, setMessage] = useState(() => leadNotice?.message ?? '');
    const [statusAlerta, setStatusAlerta] = useState(() => leadNotice?.status ?? '');
    const [openAlerta, setOpenAlerta] = useState(() => Boolean(leadNotice?.message));
    const [openDiscardDialog, setOpenDiscardDialog] = useState(false);
    const [openAiAgentContractDialog, setOpenAiAgentContractDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGeneratingProposal, setIsGeneratingProposal] = useState(false);
    const [leadUnits, setLeadUnits] = useState([]);
    const [initialLeadUnits, setInitialLeadUnits] = useState([]);
    const [isLoadingUnits, setIsLoadingUnits] = useState(false);
    const [editingUnits, setEditingUnits] = useState({});
    const [savingUnits, setSavingUnits] = useState({});
    const [deletingUnits, setDeletingUnits] = useState({});
    const [expandedUnits, setExpandedUnits] = useState({});
    const [unitPdfFile, setUnitPdfFile] = useState(null);
    const [isInsertingUnit, setIsInsertingUnit] = useState(false);
    const [isCheckingSerasa, setIsCheckingSerasa] = useState(false);
    const [showUnitUploadBox, setShowUnitUploadBox] = useState(false);
    const [showManualUnitForm, setShowManualUnitForm] = useState(false);
    const [manualUnitForm, setManualUnitForm] = useState(createManualUnitForm);
    const [focusedLeadField, setFocusedLeadField] = useState(null);
    const [editingLeadField, setEditingLeadField] = useState(null);
    const editStartValueRef = useRef(null);
    const editStartedAtRef = useRef(0);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [clientUnits, setClientUnits] = useState([]);
    const [clientContacts, setClientContacts] = useState(CRM_CLIENT_EMPTY_CONTACTS);
    const [clientProtocols, setClientProtocols] = useState([]);
    const [clientFinance, setClientFinance] = useState(CRM_CLIENT_EMPTY_FINANCE);
    const [showContactForm, setShowContactForm] = useState(false);
    const [isSavingContact, setIsSavingContact] = useState(false);
    const [showPortalForm, setShowPortalForm] = useState(false);
    const [isSavingPortalPassword, setIsSavingPortalPassword] = useState(false);
    const [isSavingPortalEmail, setIsSavingPortalEmail] = useState(false);
    const [isCopyingPortalUser, setIsCopyingPortalUser] = useState(false);
    const [isSendingEmailVerification, setIsSendingEmailVerification] = useState(false);
    const [openEmailVerificationDialog, setOpenEmailVerificationDialog] = useState(false);
    const loadedClientTabsRef = useRef({});
    const [clientTabState, setClientTabState] = useState({});
    const [financePage, setFinancePage] = useState(1);
    const [busyLancamento, setBusyLancamento] = useState(null);
    const [busyUnitId, setBusyUnitId] = useState(null);
    const [openDistractDialog, setOpenDistractDialog] = useState(false);
    const [distractUnitNames, setDistractUnitNames] = useState([]);
    const [distractDocuments, setDistractDocuments] = useState([]);
    const [isLoadingDistractUnits, setIsLoadingDistractUnits] = useState(false);
    const [isDistracting, setIsDistracting] = useState(false);

    useEffect(() => {
        if (!leadNotice && initialTab === 'lead') return;
        navigate(location.pathname, { replace: true, state: { ...location.state, leadNotice: undefined, initialTab: undefined } });
    }, []);

    useEffect(() => {
        if (loadedLeadIdRef.current === String(leadId)) return undefined;

        let ignore = false;
        setLeadLoadState('loading');
        setLeadData(null);
        setInitialLeadData(null);

        const loadLead = async () => {
            try {
                const lead = await fetchCrmLeadById(leadId, { isPhaseLocked: !canViewLeadUnits });
                if (ignore) return;

                if (!lead) {
                    setLeadLoadState('not_found');
                    return;
                }

                loadedLeadIdRef.current = String(leadId);
                setLeadData({ ...lead });
                setInitialLeadData({ ...lead });
                setLeadLoadState('ready');
            } catch (error) {
                if (ignore) return;
                setLeadLoadState('error');
                setMessage(error.message);
                setStatusAlerta('error');
                setOpenAlerta(true);
            }
        };

        loadLead();
        return () => { ignore = true; };
    }, [canViewLeadUnits, leadId, leadReloadKey]);

    const hasChanges = useMemo(() => {
        if (!leadData || !initialLeadData) return false;
        return JSON.stringify(leadData) !== JSON.stringify(initialLeadData);
    }, [leadData, initialLeadData]);
    const changedFieldCount = useMemo(() => countChangedFields(leadData, initialLeadData), [leadData, initialLeadData]);

    const isSellerEditable = canOverrideSellerLock || canEditSeller(leadData?.vendedor);
    const hasClientRegistration = initialLeadData !== null && initialLeadData.cliente_id !== null;
    const isCompletedLead = matchesLeadOption(initialLeadData, 'fase', 'fase_id', WON_PHASE);
    const isAiAgentSellerLead = matchesLeadOption(initialLeadData, 'vendedor', 'vendedor_id', AI_AGENT_SELLER_OPTION);
    const hasAutomatedPhase = matchesLeadOption(initialLeadData, 'fase', 'fase_id', CONTRACT_PHASE) ||
        matchesLeadOption(initialLeadData, 'fase', 'fase_id', WON_PHASE);

    const canCreateDocument = useMemo(() => {
        if (isCompletedLead) return false;
        if (hasChanges) return false;
        if (!initialLeadData) return false;
        if (initialLeadData.cliente_id !== null) return false;

        const hasRequiredLeadData = DOCUMENT_REQUIRED_FIELDS.every(({ field }) => hasDocumentFieldValue(initialLeadData[field]));

        return leadUnits.length > 0 && hasRequiredLeadData;
    }, [hasChanges, initialLeadData, isCompletedLead, leadUnits]);
    const canStillCreateContract = !isCompletedLead && !hasClientRegistration;
    const canCreateAdditive = isCompletedLead && hasClientRegistration && !hasChanges;
    const canGenerateProposal = hasDocumentFieldValue(leadData?.nome) && hasDocumentFieldValue(leadData?.ticket);

    const documentBlockReason = useMemo(() => {
        if (!initialLeadData) return '';
        if (isCompletedLead) return 'Lead conquistado não gera contrato novo.';
        if (initialLeadData.cliente_id !== null) return 'O lead já possui matrícula.';
        if (hasChanges) return 'Salve as alterações antes de gerar o contrato.';

        const missingFields = DOCUMENT_REQUIRED_FIELDS.filter(({ field }) => !hasDocumentFieldValue(initialLeadData[field]));
        if (missingFields.length > 0) {
            return `Preencha ${joinLabelsPtBr(missingFields.map(({ label }) => label))} para gerar o contrato.`;
        }

        if (isLoadingUnits) return 'Carregando as unidades do lead...';
        if (leadUnits.length === 0) return 'Insira ao menos uma unidade para gerar o contrato.';
        return '';
    }, [hasChanges, initialLeadData, isCompletedLead, isLoadingUnits, leadUnits]);
    const additiveBlockReason = hasChanges ? 'Salve as alterações antes de gerar o aditivo.' : '';
    const proposalBlockReason = useMemo(() => {
        const missingFields = [
            !hasDocumentFieldValue(leadData?.nome) && 'nome',
            !hasDocumentFieldValue(leadData?.ticket) && 'ticket'
        ].filter(Boolean);

        return missingFields.length > 0 ? `Informe ${joinLabelsPtBr(missingFields)} para gerar a proposta.` : '';
    }, [leadData?.nome, leadData?.ticket]);

    const changedUnits = useMemo(() => leadUnits.reduce((acc, unit, index) => {
        if (!areUnitsEqual(unit, initialLeadUnits[index])) {
            acc[index] = true;
        }
        return acc;
    }, {}), [initialLeadUnits, leadUnits]);

    const updateLeadField = (field, value) => {
        if (isCompletedLead && !(
            field === 'dia_vencimento' ||
            (field === 'vendedor' && canOverrideSellerLock)
        )) return;
        if (field === 'desconto') {
            if (isCompletedLead && !isSupervision) return;

            const nextDesconto = parseDiscount(value);
            if (nextDesconto === null || nextDesconto < DESCONTO_MIN || nextDesconto > DESCONTO_MAX) return;
        }
        if (field === 'status' && value === COMPLETED_STATUS.label) return;
        if (field === 'fase' && (
            hasAutomatedPhase ||
            value === CONTRACT_PHASE.label ||
            value === WON_PHASE.label
        )) return;

        const nextValue = field === 'obs'
            ? value.slice(0, OBS_MAX_LENGTH)
            : field === 'cpf'
                ? maskCpfCnpj(value)
                : value;
        setLeadData((prev) => ({ ...prev, [field]: nextValue }));
    };

    const updateLeadUnitField = (unitIndex, field, value) => {
        if (isCompletedLead) return;
        setLeadUnits((prev) => prev.map((unit, index) => (
            index === unitIndex ? { ...unit, [field]: value } : unit
        )));
    };

    const updateLeadUnitAddressField = (unitIndex, field, value) => {
        if (isCompletedLead) return;
        setLeadUnits((prev) => prev.map((unit, index) => (
            index === unitIndex
                ? {
                    ...unit,
                    endereco: {
                        ...(unit.endereco ?? {}),
                        [field]: value
                    }
                }
                : unit
        )));
    };

    useEffect(() => {
        const fetchLeadUnits = async () => {
            if (!leadData?.lead_id) return;

            setIsLoadingUnits(true);
            const response = await fetchFlask(CRM_LEAD_UNITS_API, JSON.stringify({ lead_id: leadData.lead_id }));

            if (response?.status === 'success') {
                const units = Array.isArray(response.data) ? response.data : [];
                setLeadUnits(units);
                setInitialLeadUnits(units.map((unit) => ({ ...unit, endereco: unit.endereco ? { ...unit.endereco } : unit.endereco })));
                setEditingUnits({});
                setSavingUnits({});
                setDeletingUnits({});
                setExpandedUnits({});
            } else {
                setLeadUnits([]);
                setInitialLeadUnits([]);
                setDeletingUnits({});
                setExpandedUnits({});
            }

            setIsLoadingUnits(false);
        };

        fetchLeadUnits();
    }, [leadData?.lead_id]);

    const clienteId = leadData?.cliente_id ?? null;
    const hasMatricula = Boolean(clienteId);

    useEffect(() => {
        setFinancePage(1);
        setShowContactForm(false);
        setShowPortalForm(false);
    }, [clienteId]);

    useEffect(() => {
        const loaders = {
            unidades: [fetchClientUnits, setClientUnits],
            contato: [fetchClientContacts, setClientContacts],
            protocolos: [fetchClientProtocols, setClientProtocols],
            financeiro: [
                (id) => fetchClientFinance(id, financePage, FINANCE_PAGE_SIZE),
                setClientFinance
            ]
        };
        const loader = loaders[activeTab];
        // A chave leva o cliente junto para que trocar de matricula recarregue as abas,
        // e a pagina no caso do financeiro, que agora e paginado no servidor: o estado
        // guarda uma pagina so, entao voltar para a anterior precisa buscar de novo.
        const tabKey = activeTab === 'financeiro'
            ? `${clienteId}:financeiro:${financePage}`
            : `${clienteId}:${activeTab}`;

        // O controle fica em ref, e nao em state, porque em state ele entraria nas
        // dependencias e o efeito se realimentaria: o re-run cancelava a busca em voo
        // e o spinner nunca era apagado.
        if (!clienteId || !loader || loadedClientTabsRef.current[tabKey]) return;

        const [fetchTabData, applyTabData] = loader;

        // Marca antes de buscar para nao disparar a mesma aba duas vezes.
        loadedClientTabsRef.current[tabKey] = true;
        setClientTabState((current) => ({ ...current, [tabKey]: 'loading' }));

        const loadClientTab = async () => {
            try {
                applyTabData(await fetchTabData(clienteId));
                setClientTabState((current) => ({ ...current, [tabKey]: 'ready' }));
            } catch (error) {
                // Libera a chave para que voltar na aba tente de novo.
                loadedClientTabsRef.current[tabKey] = false;
                setClientTabState((current) => ({ ...current, [tabKey]: 'error' }));
                setMessage(error.message);
                setStatusAlerta('error');
                setOpenAlerta(true);
            }
        };

        loadClientTab();
    }, [activeTab, clienteId, financePage]);

    // Sem estado registrado a aba ainda nao buscou, entao ja conta como carregando
    // e evita o piscar de "nada por aqui" antes do efeito rodar.
    const isClientTabLoading = (tab) => {
        if (!hasMatricula) return false;

        const state = clientTabState[tab === 'financeiro'
            ? `${clienteId}:financeiro:${financePage}`
            : `${clienteId}:${tab}`];

        return state === undefined || state === 'loading';
    };

    const contactUnitOptions = useMemo(() => getContactUnitOptions(clientContacts), [clientContacts]);
    const canUpdateContact = isCompletedLead && hasMatricula && contactUnitOptions.length > 0;

    const saveClientContact = async (unidades, form) => {
        if (!canUpdateContact) return false;

        if (unidades.length === 0) {
            setMessage('Selecione ao menos uma unidade para alterar o contato.');
            setStatusAlerta('warning');
            setOpenAlerta(true);
            return false;
        }

        if (!hasContactUpdateValues(form)) {
            setMessage('Preencha ao menos um campo para alterar o contato.');
            setStatusAlerta('warning');
            setOpenAlerta(true);
            return false;
        }

        setIsSavingContact(true);
        const response = await updateClientContact(unidades, form);

        setMessage(response?.message);
        setStatusAlerta(response?.status);
        setOpenAlerta(true);

        const isSaved = response?.status === 'success';

        if (isSaved || response?.status === 'warning') {
            setClientContacts(await fetchClientContacts(clienteId));
        }

        if (isSaved) {
            setShowContactForm(false);
        }

        setIsSavingContact(false);

        return isSaved;
    };

    const canDeactivateUnit = isCompletedLead && hasMatricula;

    const deactivateUnit = async (unit) => {
        if (!canDeactivateUnit || busyUnitId !== null) return false;

        setBusyUnitId(unit.unidade_id);
        const response = await deactivateClientUnit(unit);

        setMessage(response?.message);
        setStatusAlerta(response?.status);
        setOpenAlerta(true);

        const isDone = response?.status === 'success';

        if (isDone) {
            setClientUnits(await fetchClientUnits(clienteId));
        }

        setBusyUnitId(null);

        return isDone;
    };

    const canDistract = canManageDistract && isCompletedLead && hasMatricula;

    const openClientDistract = async () => {
        if (!canDistract) return;

        setDistractUnitNames([]);
        setDistractDocuments([]);
        setOpenDistractDialog(true);
        setIsLoadingDistractUnits(true);

        try {
            const [units, contacts] = await Promise.all([
                fetchClientUnits(clienteId),
                fetchClientContacts(clienteId)
            ]);
            setClientUnits(units);
            setClientContacts(contacts);
            setDistractUnitNames(getDistractUnitNames(units));
            setDistractDocuments(getDistractDocuments(initialLeadData?.cpf, contacts));
        } catch (error) {
            setOpenDistractDialog(false);
            setMessage(error.message);
            setStatusAlerta('error');
            setOpenAlerta(true);
        } finally {
            setIsLoadingDistractUnits(false);
        }
    };

    const confirmClientDistract = async ({ motivo, data, cpfCnpj, representativeName, representativeCpf }) => {
        if (!canDistract || isDistracting || distractUnitNames.length === 0 || !cpfCnpj) return;

        setIsDistracting(true);
        const response = await distractClient({
            clienteId,
            cliente: clientContacts.cliente?.cliente || initialLeadData?.nome || '',
            unidades: distractUnitNames,
            data,
            motivo,
            cpfCnpj,
            representativeName,
            representativeCpf
        });

        setMessage(response?.message);
        setStatusAlerta(response?.status);
        setOpenAlerta(true);

        if (response?.status === 'success') {
            setOpenDistractDialog(false);
            setClientUnits(await fetchClientUnits(clienteId));
        }

        setIsDistracting(false);
    };

    const runReceivableAction = async (codigoLancamento, request) => {
        if (busyLancamento !== null) return false;

        setBusyLancamento(codigoLancamento);
        const response = await request();

        setMessage(response?.message);
        setStatusAlerta(response?.status);
        setOpenAlerta(true);

        const isDone = response?.status === 'success';

        if (isDone) {
            setClientFinance(await fetchClientFinance(clienteId, financePage, FINANCE_PAGE_SIZE));
        }

        setBusyLancamento(null);

        return isDone;
    };

    const cancelClientReceivable = (codigoLancamento, obs) => runReceivableAction(
        codigoLancamento,
        () => cancelReceivable(codigoLancamento, obs)
    );

    const extendClientReceivable = (codigoLancamento, vencimento) => runReceivableAction(
        codigoLancamento,
        () => extendReceivable(codigoLancamento, vencimento)
    );

    const canUpdatePortalLogin = isCompletedLead && hasMatricula;

    const handlePortalResponse = async (response) => {
        setMessage(response?.message);
        setStatusAlerta(response?.status);
        setOpenAlerta(true);

        const isSaved = response?.status === 'success';

        if (isSaved) {
            setClientContacts(await fetchClientContacts(clienteId));
        }

        return isSaved;
    };

    const changePortalPassword = async () => {
        if (!canUpdatePortalLogin || isSavingPortalPassword) return false;

        setIsSavingPortalPassword(true);
        const isSaved = await handlePortalResponse(await updatePortalPassword(clienteId));
        setIsSavingPortalPassword(false);

        return isSaved;
    };

    const changePortalEmail = async (email) => {
        if (!canUpdatePortalLogin || isSavingPortalEmail) return false;

        if (email.trim() === '') {
            setMessage('Informe o novo e-mail do portal.');
            setStatusAlerta('warning');
            setOpenAlerta(true);
            return false;
        }

        setIsSavingPortalEmail(true);
        const isSaved = await handlePortalResponse(await updatePortalEmail(clienteId, email));
        setIsSavingPortalEmail(false);

        return isSaved;
    };

    const copyPortalUserToFakeUser = async () => {
        if (!canUpdatePortalLogin || isCopyingPortalUser) return;

        setIsCopyingPortalUser(true);
        const response = await copyPortalUser(clienteId);

        setMessage(response?.message);
        setStatusAlerta(response?.status);
        setOpenAlerta(true);
        setIsCopyingPortalUser(false);
    };

    const contractEmail = (leadData?.email ?? '').trim();
    const canSendEmailVerification = contractEmail !== '';
    const hasValidPhone = isValidPhone(leadData?.telefone);
    const phoneDisabledReason = hasValidPhone ? '' : 'Telefone em formato inválido.';

    const sendEmailVerification = async () => {
        if (!canSendEmailVerification || isSendingEmailVerification) return;

        setIsSendingEmailVerification(true);
        const response = await sendEmailVerificationCode(contractEmail);

        setMessage(response?.message);
        setStatusAlerta(response?.status);
        setOpenAlerta(true);
        setIsSendingEmailVerification(false);
    };

    const hasFinanceRows = clientFinance.contas_a_receber.total > 0 ||
        clientFinance.multas.length > 0 ||
        clientFinance.acordos.length > 0;

    const saveChanges = async () => {
        if (!hasChanges) return;

        const nextLeadData = isCompletedLead
            ? {
                ...initialLeadData,
                dia_vencimento: leadData.dia_vencimento,
                ...(canOverrideSellerLock ? { vendedor: leadData.vendedor } : {})
            }
            : leadData;

        setIsSubmitting(true);
        const response = await fetchFlask(CRM_LEAD_UPDATE_API, JSON.stringify(buildPayload(nextLeadData)));

        setMessage(response.message);
        setStatusAlerta(response.status);
        setOpenAlerta(true);

        if (response.status === 'success') {
            queryClient.setQueriesData({ queryKey: ['crm', 'leads'] }, (oldData) => {
                if (!oldData || !Array.isArray(oldData.rows)) return oldData;
                return {
                    ...oldData,
                    rows: oldData.rows.map((row) => (row.lead_id === leadData.lead_id ? { ...row, ...nextLeadData } : row))
                };
            });
            setLeadData({ ...nextLeadData });
            setInitialLeadData({ ...nextLeadData });
        }

        setIsSubmitting(false);
    };

    const handleDiscardChanges = () => {
        if (!hasChanges) {
            navigateBackToLeads();
            return;
        }
        setOpenDiscardDialog(true);
    };

    const confirmDiscardChanges = () => {
        setLeadData(initialLeadData);
        setOpenDiscardDialog(false);
    };

    const handleCreateDocument = async () => {
        if (isCompletedLead || !canCreateDocument) return;

        const leadDocumentDigits = getDigits(initialLeadData.cpf);
        const isPessoaFisica = leadDocumentDigits.length !== CNPJ_DIGITS_LENGTH;
        const leadNameParts = (initialLeadData.nome ?? '').toString().trim().split(/\s+/).filter(Boolean);

        if (isPessoaFisica && leadNameParts.length < 2) {
            setMessage('Lead pessoa física precisa ter nome completo.');
            setStatusAlerta('error');
            setOpenAlerta(true);
            return;
        }

        if (!isValidPhone(initialLeadData.telefone)) {
            setMessage(`Telefone do lead está em formato inválido (${initialLeadData.telefone}). Verifique o cadastro antes de gerar o contrato.`);
            setStatusAlerta('error');
            setOpenAlerta(true);
            return;
        }

        setIsCheckingSerasa(true);
        const response = await fetchFlask(CRM_LEAD_SERASA_API, JSON.stringify({
            document: initialLeadData.cpf,
            name: initialLeadData.nome,
            lead_id: initialLeadData.lead_id
        }));

        if (response.status === 'success' && !(isAiAgentSellerLead && canSkipAgentContractForm)) {
            navigate(`/CRM/leads/${initialLeadData.lead_id}/contrato`, {
                state: {
                    serasaAprovado: true,
                    serasaMessage: response.message,
                    serasaStatus: response.status,
                    leadData: initialLeadData,
                    leadUnits,
                    leadListState
                }
            });
            return;
        }

        setMessage(response.message);
        setStatusAlerta(response.status);
        setOpenAlerta(true);
        if (response.status === 'success') {
            const formData = new FormData();
            formData.append('lead_id', String(initialLeadData.lead_id));
            const contractResponse = await fetchFlask('createDocuments', formData);
            setMessage(contractResponse.message);
            setStatusAlerta(contractResponse.status);
            setOpenAlerta(true);
        }
        setIsCheckingSerasa(false);
    };

    const handleCreateDocumentClick = () => {
        if (isCompletedLead || !canCreateDocument || isCheckingSerasa) return;
        if (isAiAgentSellerLead) {
            setOpenAiAgentContractDialog(true);
            return;
        }
        handleCreateDocument();
    };

    const handleCreateAdditive = () => {
        if (!canCreateAdditive) return;

        navigate('/CRM/generateAdditive', {
            state: {
                leadData: initialLeadData,
                leadUnits
            }
        });
    };

    const handleGenerateProposal = async () => {
        if (!canGenerateProposal || isGeneratingProposal) return;

        setIsGeneratingProposal(true);
        const response = await fetchFlask(PROPOSTA_DIGITAL_API, JSON.stringify({
            nome: leadData.nome,
            valor: leadData.ticket
        }));

        if (response.status !== 'error') {
            const link = document.createElement('a');
            link.href = response.url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }

        setMessage(response.message);
        setStatusAlerta(response.status);
        setOpenAlerta(true);
        setIsGeneratingProposal(false);
    };

    const copyText = async (text, successMessage) => {
        try {
            await navigator.clipboard.writeText(text);
            setMessage(successMessage);
            setStatusAlerta('success');
        } catch (error) {
            setMessage('Não foi possível copiar. Copie manualmente.');
            setStatusAlerta('error');
        }
        setOpenAlerta(true);
    };

    const handleCopyReferralLink = async () => {
        await copyText(
            `https://settaenergia.com.br/?cliente_id=${initialLeadData.cliente_id}&origin=app`,
            'Link de indicação copiado com sucesso.'
        );
    };

    const handleEditUnit = (unitIndex) => {
        if (isCompletedLead) return;
        setEditingUnits((prev) => ({ ...prev, [unitIndex]: true }));
        setExpandedUnits((prev) => ({ ...prev, [unitIndex]: true }));
    };

    const handleCancelUnitEdit = (unitIndex) => {
        setLeadUnits((prev) => prev.map((unit, index) => (
            index === unitIndex ? initialLeadUnits[unitIndex] : unit
        )));
        setEditingUnits((prev) => ({ ...prev, [unitIndex]: false }));
    };

    const deleteLeadUnit = async (unitIndex) => {
        if (isCompletedLead) return;

        const unit = leadUnits[unitIndex];
        const codigoDoCliente = unit?.codigo_do_cliente;

        if (!leadData?.lead_id || !codigoDoCliente) {
            setMessage('Unidade sem código do cliente. Não foi possível excluir.');
            setStatusAlerta('error');
            setOpenAlerta(true);
            return;
        }

        setDeletingUnits((prev) => ({ ...prev, [unitIndex]: true }));
        const response = await fetchFlask(CRM_LEAD_UNITS_DELETE_API, JSON.stringify({
            unit_id: unit.unidade_id
        }));

        setMessage(response.message);
        setStatusAlerta(response.status);
        setOpenAlerta(true);

        if (response.status === 'success') {
            setLeadUnits((prev) => prev.filter((_, index) => index !== unitIndex));
            setInitialLeadUnits((prev) => prev.filter((_, index) => index !== unitIndex));
            setEditingUnits({});
            setSavingUnits({});
            setDeletingUnits({});
            setExpandedUnits({});
            return;
        }

        setDeletingUnits((prev) => ({ ...prev, [unitIndex]: false }));
    };

    const toggleUnitExpansion = (unitIndex) => {
        setExpandedUnits((prev) => ({ ...prev, [unitIndex]: !prev[unitIndex] }));
    };

    const updateManualUnitField = (field, value) => {
        if (isCompletedLead) return;
        setManualUnitForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateManualUnitAddressField = (field, value) => {
        if (isCompletedLead) return;
        setManualUnitForm((prev) => ({
            ...prev,
            endereco: {
                ...(prev.endereco ?? {}),
                [field]: value
            }
        }));
    };

    const insertLeadUnits = async () => {
        if (isCompletedLead || !leadData) return;

        const leadCpf = (leadData.cpf ?? '').toString().trim();

        if (!leadCpf) {
            setMessage('Lead sem CPF. Não é possível inserir unidade sem esse dado.');
            setStatusAlerta('error');
            setOpenAlerta(true);
            return;
        }

        const hasManualUnitData = editableUnitFieldList.some((field) => (manualUnitForm[field] ?? '').toString().trim() !== '') ||
            editableAddressFieldList.some((field) => (manualUnitForm.endereco?.[field] ?? '').toString().trim() !== '');
        const hasAllRequiredManualFields = requiredManualUnitFieldList.every((field) => (manualUnitForm[field] ?? '').toString().trim() !== '') &&
            requiredManualAddressFieldList.every((field) => (manualUnitForm.endereco?.[field] ?? '').toString().trim() !== '');
        const shouldInsertManualUnit = showManualUnitForm && hasManualUnitData;

        if (!unitPdfFile) {
            setMessage('Envie um PDF para inserir a unidade.');
            setStatusAlerta('error');
            setOpenAlerta(true);
            return;
        }

        if (showManualUnitForm && !hasAllRequiredManualFields) {
            setMessage('Preencha todos os campos obrigatórios da unidade.');
            setStatusAlerta('error');
            setOpenAlerta(true);
            return;
        }

        if (shouldInsertManualUnit && !isValidConsumoMedio(manualUnitForm.consumo_medio)) {
            setMessage(INVALID_CONSUMO_MEDIO_MESSAGE);
            setStatusAlerta('error');
            setOpenAlerta(true);
            return;
        }

        const formData = new FormData();
        formData.append('lead_id', String(leadData.lead_id));
        formData.append('contaEnergia', unitPdfFile);
        formData.append('nome', String(leadData.nome ?? ''));
        formData.append('cpf', String(leadData.cpf ?? ''));
        if (shouldInsertManualUnit) {
            formData.append('unit', JSON.stringify(buildInsertUnitPayload(manualUnitForm)));
        }

        setIsInsertingUnit(true);
        const response = await fetchFlask(CRM_LEAD_UNITS_INSERT_API, formData);
        const returnedUnits = extractLeadUnitsFromInsertResponse(response);
        const hasReturnedUnits = returnedUnits.length > 0;
        const isSuccess = response?.status === 'success' || hasReturnedUnits;

        if (response?.message) {
            setMessage(response.message);
            setStatusAlerta(response.status ?? (isSuccess ? 'success' : 'error'));
            setOpenAlerta(true);
        }

        if (isSuccess) {
            if (hasReturnedUnits) {
                const normalizedUnits = returnedUnits.map((unit) => ({
                    ...unit,
                    endereco: unit?.endereco ? { ...unit.endereco } : unit?.endereco
                }));
                setLeadUnits((prev) => mergeLeadUnitsAfterInsert(prev, normalizedUnits));
                setInitialLeadUnits((prev) => mergeLeadUnitsAfterInsert(prev, normalizedUnits));
            }
            setEditingUnits({});
            setSavingUnits({});
            setDeletingUnits({});
            setExpandedUnits({});
            setUnitPdfFile(null);
            setShowManualUnitForm(false);
            setManualUnitForm(createManualUnitForm());
            setMessage('Unidade adicionada!');
            setStatusAlerta('success');
            setOpenAlerta(true);
        } else {
            setMessage(response?.message ?? 'Erro ao inserir unidade.');
            setStatusAlerta(response?.status ?? 'error');
            setOpenAlerta(true);
            if (unitPdfFile && !shouldInsertManualUnit) {
                setShowManualUnitForm(true);
            }
        }

        setIsInsertingUnit(false);
    };

    const saveUnitChanges = async (unitIndex) => {
        if (isCompletedLead) return;

        const unit = leadUnits[unitIndex];
        const initialUnit = initialLeadUnits[unitIndex];

        if (!unit || !initialUnit || areUnitsEqual(unit, initialUnit)) return;

        const missingAddressFields = getMissingUnitAddressFields(unit.endereco);
        if (missingAddressFields.length > 0) {
            setMessage(`Preencha ${joinLabelsPtBr(missingAddressFields.map(getUnitFieldLabel))} da unidade.`);
            setStatusAlerta('error');
            setOpenAlerta(true);
            return;
        }

        if (!isValidConsumoMedio(unit.consumo_medio)) {
            setMessage(INVALID_CONSUMO_MEDIO_MESSAGE);
            setStatusAlerta('error');
            setOpenAlerta(true);
            return;
        }

        setSavingUnits((prev) => ({ ...prev, [unitIndex]: true }));

        const response = await fetchFlask(CRM_LEAD_UNITS_UPDATE_API, JSON.stringify({
            lead_id: leadData.lead_id,
            unit: buildUnitPayload(unit)
        }));

        setMessage(response.message);
        setStatusAlerta(response.status);
        setOpenAlerta(true);

        if (response.status === 'success') {
            setInitialLeadUnits((prev) => prev.map((item, index) => (
                index === unitIndex ? { ...unit, endereco: unit.endereco ? { ...unit.endereco } : unit.endereco } : item
            )));
            setEditingUnits((prev) => ({ ...prev, [unitIndex]: false }));
        }

        setSavingUnits((prev) => ({ ...prev, [unitIndex]: false }));
    };

    if (!leadData || !initialLeadData) {
        const isLoadingLead = leadLoadState === 'loading';
        const isMissingLead = leadLoadState === 'not_found';

        return (
            <div className='mainBox crm-lead-details-page'>
                <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />
                <div className='crm-lead-details-shell'>
                    <div className='crm-lead-details-card crm-lead-details-load-state' role='status'>
                        {isLoadingLead
                            ? (
                                <>
                                    <CircularProgress size={28} />
                                    <span className='crm-lead-details-load-copy'>Carregando lead #{leadId}...</span>
                                </>
                                )
                            : (
                                <>
                                    <strong className='crm-lead-details-load-title'>
                                        {isMissingLead ? `Lead #${leadId} não encontrado` : 'Não foi possível carregar o lead'}
                                    </strong>
                                    <span className='crm-lead-details-load-copy'>
                                        {isMissingLead
                                            ? 'Confira o número do lead ou se você tem acesso a ele.'
                                            : 'Verifique a conexão e tente novamente.'}
                                    </span>
                                    <div className='crm-lead-details-load-actions'>
                                        {!isMissingLead && (
                                            <button
                                                type='button'
                                                className='crm-lead-details-button crm-lead-details-button-primary'
                                                onClick={() => setLeadReloadKey((prev) => prev + 1)}
                                            >
                                                <RefreshRoundedIcon fontSize='inherit' />
                                                <span>Tentar novamente</span>
                                            </button>
                                        )}
                                        <button
                                            type='button'
                                            className='crm-lead-details-button crm-lead-details-button-secondary'
                                            onClick={navigateBackToLeads}
                                        >
                                            <ArrowBackRoundedIcon fontSize='inherit' />
                                            <span>Voltar para a lista</span>
                                        </button>
                                    </div>
                                </>
                                )}
                    </div>
                </div>
            </div>
        );
    }

    const getLeadFieldLabel = (item) => (item.field === 'cpf' ? getCpfCnpjLabel(leadData.cpf) : item.label);

    const getLeadFieldLockReason = (item) => {
        if (item.readOnly) return 'Este campo não pode ser alterado aqui.';
        if (item.field === 'vendedor' && (!isSellerEditable || (isCompletedLead && !canOverrideSellerLock))) {
            return 'Vendedor já definido. Só a supervisão pode trocar.';
        }
        if (isCompletedLead && item.field !== 'dia_vencimento' && item.field !== 'vendedor') {
            return 'Lead conquistado: campo bloqueado para edição.';
        }
        if (item.field === 'fase' && hasAutomatedPhase) return 'Fase atualizada automaticamente pelo contrato.';
        return '';
    };

    const finishLeadFieldEdit = () => {
        setEditingLeadField(null);
        setFocusedLeadField(null);
    };

    const startLeadFieldEdit = (field) => {
        editStartValueRef.current = leadData[field];
        editStartedAtRef.current = Date.now();
        setEditingLeadField(field);
    };

    const cancelLeadFieldEdit = (field) => {
        const startValue = editStartValueRef.current;
        setLeadData((prev) => ({ ...prev, [field]: startValue }));
        finishLeadFieldEdit();
    };

    const handleLeadFieldKeyDown = (event, item) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            cancelLeadFieldEdit(item.field);
            return;
        }
        if (event.key === 'Enter' && item.kind !== 'obs') {
            event.preventDefault();
            finishLeadFieldEdit();
        }
    };

    const renderLeadFieldInput = (item) => {
        const label = getLeadFieldLabel(item);

        if (item.kind === 'select' || item.kind === 'dueDay') {
            const options = item.kind === 'dueDay'
                ? DIA_VENCIMENTO_OPTIONS.map((dueDay) => ({ id: dueDay, label: dueDay }))
                : item.options;

            return (
                <FormControl className='crm-lead-details-select crm-dashboard-filter-control crm-dashboard-bordered-select' fullWidth>
                    <InputLabel id={`${item.field}-detail-label`}>{label}</InputLabel>
                    <Select
                        labelId={`${item.field}-detail-label`}
                        label={label}
                        value={leadData[item.field] ?? ''}
                        onChange={(e) => {
                            if (Date.now() - editStartedAtRef.current < SELECT_ACCIDENTAL_PICK_MS) return;
                            updateLeadField(item.field, e.target.value);
                        }}
                        onClose={finishLeadFieldEdit}
                        defaultOpen
                        autoFocus
                        IconComponent={KeyboardArrowDownRoundedIcon}
                        MenuProps={selectMenuProps}
                    >
                        {options.map((option) => (
                            <MenuItem
                                className='crm-dashboard-select-menu-item'
                                key={option.id}
                                value={option.label}
                                disabled={(item.field === 'status' && option.id === COMPLETED_STATUS.id) ||
                                    (item.field === 'fase' && AUTOMATED_PHASE_IDS.has(option.id))}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        if (item.kind === 'obs') {
            return (
                <TextField
                    className='crm-lead-details-text-field'
                    label={label}
                    value={leadData.obs ?? ''}
                    onChange={(e) => updateLeadField('obs', e.target.value)}
                    onBlur={finishLeadFieldEdit}
                    onKeyDown={(e) => handleLeadFieldKeyDown(e, item)}
                    autoFocus
                    fullWidth
                    multiline
                    minRows={4}
                    inputProps={{ maxLength: OBS_MAX_LENGTH }}
                    helperText={`${(leadData.obs ?? '').length}/${OBS_MAX_LENGTH} · Esc desfaz`}
                />
            );
        }

        return (
            <TextField
                className='crm-lead-details-text-field'
                label={label}
                type={item.type ?? 'text'}
                value={getLeadFieldDisplayValue(item.field, leadData[item.field], focusedLeadField)}
                onChange={(e) => updateLeadField(item.field, e.target.value)}
                onFocus={() => setFocusedLeadField(item.field)}
                onBlur={finishLeadFieldEdit}
                onKeyDown={(e) => handleLeadFieldKeyDown(e, item)}
                autoFocus
                fullWidth
            />
        );
    };

    const renderLeadField = (item) => {
        const label = getLeadFieldLabel(item);
        const lockReason = getLeadFieldLockReason(item);
        const isEditing = editingLeadField === item.field && !lockReason;
        const hasValue = hasDocumentFieldValue(leadData[item.field]);
        const displayValue = hasValue ? getLeadFieldDisplayValue(item.field, leadData[item.field], null) : '';
        const isMissingForContract = !hasValue && canStillCreateContract && DOCUMENT_REQUIRED_FIELD_NAMES.has(item.field);
        const isChanged = JSON.stringify(leadData[item.field]) !== JSON.stringify(initialLeadData[item.field]);
        const fieldClassName = [
            'crm-lead-details-field',
            item.span,
            isMissingForContract ? 'is-missing' : '',
            isChanged ? 'is-changed' : ''
        ].filter(Boolean).join(' ');

        if (isEditing) {
            return (
                <div key={item.field} className={`${fieldClassName} is-editing`}>
                    {renderLeadFieldInput(item)}
                </div>
            );
        }

        const valueContent = (
            <span className={`crm-lead-details-read-text${hasValue ? '' : ' is-empty'}`}>
                {hasValue ? displayValue : '—'}
            </span>
        );

        return (
            <div key={item.field} className={fieldClassName}>
                <div className='crm-lead-details-read-field'>
                    <span className='crm-lead-details-read-label'>
                        {label}
                        {isChanged && <span className='crm-lead-details-read-flag is-changed'>alterado</span>}
                        {isMissingForContract && <span className='crm-lead-details-read-flag is-missing'>falta para o contrato</span>}
                    </span>
                    {lockReason
                        ? (
                            <Tooltip title={lockReason} arrow placement='bottom-start'>
                                <span className='crm-lead-details-read-value is-locked' tabIndex={0}>
                                    {valueContent}
                                    <LockRoundedIcon className='crm-lead-details-read-icon' fontSize='inherit' />
                                </span>
                            </Tooltip>
                            )
                        : (
                            <button
                                type='button'
                                className={`crm-lead-details-read-value${item.kind === 'obs' ? ' is-multiline' : ''}`}
                                onClick={() => startLeadFieldEdit(item.field)}
                                aria-label={`${label}: ${hasValue ? displayValue : 'vazio'}. Clique para editar.`}
                            >
                                {valueContent}
                                <EditRoundedIcon className='crm-lead-details-read-icon' fontSize='inherit' />
                            </button>
                            )}
                </div>
            </div>
        );
    };

    const renderUnitViewGrid = (unit) => (
        <div className='crm-client-field-grid'>
            {getUnitEntries(unit).map(([field, value]) => (
                field === 'endereco'
                    ? <Field key={field} label='Endereço' value={buildUnitAddressLabel(value) || '—'} wide />
                    : <Field key={field} label={getUnitFieldLabel(field)} value={formatUnitValue(value, field) || '—'} />
            ))}
        </div>
    );

    const renderUnitEditGrid = (unit, index) => (
        <div className='crm-lead-details-grid'>
            {getUnitEntries(withEditableUnitFields(unit)).map(([field, value]) => (
                field === 'endereco'
                    ? getAddressEntries(value).map(([addressField, addressValue]) => (
                        <div key={`${field}-${addressField}-${index}`} className='crm-lead-details-field is-small'>
                            <TextField
                                className={`crm-lead-details-text-field${isEditableAddressField(addressField) ? ' crm-lead-details-text-field-editable' : ' is-readonly'}`}
                                label={getUnitFieldLabel(addressField)}
                                value={formatUnitValue(addressValue, addressField)}
                                onChange={(e) => updateLeadUnitAddressField(index, addressField, e.target.value)}
                                fullWidth
                                InputProps={!isCompletedLead && isEditableAddressField(addressField) ? undefined : readOnlyInputProps}
                            />
                        </div>
                    ))
                    : (
                        <div key={`${field}-${index}`} className='crm-lead-details-field is-small'>
                            <TextField
                                className={`crm-lead-details-text-field${isEditableUnitField(field) ? ' crm-lead-details-text-field-editable' : ' is-readonly'}`}
                                label={getUnitFieldLabel(field)}
                                value={formatUnitValue(value, field)}
                                onChange={(e) => updateLeadUnitField(index, field, e.target.value)}
                                fullWidth
                                InputProps={!isCompletedLead && isEditableUnitField(field) ? undefined : readOnlyInputProps}
                            />
                        </div>
                    )
            ))}
        </div>
    );

    const renderLeadUnit = (unit, index) => {
        const isExpanded = Boolean(expandedUnits[index]);
        const isEditing = Boolean(editingUnits[index]);
        const isBusy = Boolean(savingUnits[index]) || Boolean(deletingUnits[index]);
        const activeFlags = UNIT_FLAGS.filter(({ field }) => unit?.[field] === true);
        const panelId = `lead-unit-panel-${index}`;

        return (
            <article
                key={`${leadData.lead_id}-unit-${index}`}
                className={`crm-lead-details-unit-card${isExpanded ? ' is-open' : ''}${changedUnits[index] ? ' has-changes' : ''}`}
            >
                <header className='crm-lead-details-unit-header'>
                    <button
                        type='button'
                        className='crm-lead-details-unit-toggle'
                        onClick={() => toggleUnitExpansion(index)}
                        aria-expanded={isExpanded}
                        aria-controls={panelId}
                    >
                        <span className='crm-lead-details-unit-index'>{index + 1}</span>
                        <span className='crm-lead-details-unit-identity'>
                            <span className='crm-lead-details-unit-title'>{unit?.titular || 'Titular não informado'}</span>
                            <span className='crm-lead-details-unit-subtitle'>
                                <LocationOnRoundedIcon fontSize='inherit' />
                                {getUnitStreetLabel(unit?.endereco)}
                            </span>
                        </span>
                        <KeyboardArrowDownRoundedIcon
                            className={`crm-lead-details-unit-toggle-icon${isExpanded ? ' is-open' : ''}`}
                            fontSize='inherit'
                        />
                    </button>

                    <div className='crm-lead-details-unit-actions'>
                        {!isEditing
                            ? (
                                <>
                                    <button
                                        type='button'
                                        className='crm-lead-details-button crm-lead-details-button-secondary is-compact'
                                        onClick={() => handleEditUnit(index)}
                                        disabled={isCompletedLead || isBusy}
                                    >
                                        <EditRoundedIcon fontSize='inherit' />
                                        <span>Editar</span>
                                    </button>
                                    <button
                                        type='button'
                                        className='crm-lead-details-button crm-lead-details-button-danger is-compact'
                                        onClick={() => deleteLeadUnit(index)}
                                        disabled={isCompletedLead || isBusy}
                                    >
                                        <DeleteRoundedIcon fontSize='inherit' />
                                        <span>{deletingUnits[index] ? 'Excluindo...' : 'Excluir'}</span>
                                    </button>
                                </>
                                )
                            : (
                                <>
                                    <button
                                        type='button'
                                        className='crm-lead-details-button crm-lead-details-button-warning is-compact'
                                        onClick={() => handleCancelUnitEdit(index)}
                                        disabled={Boolean(savingUnits[index])}
                                    >
                                        <UndoRoundedIcon fontSize='inherit' />
                                        <span>Cancelar</span>
                                    </button>
                                    <button
                                        type='button'
                                        className='crm-lead-details-button crm-lead-details-button-primary is-compact'
                                        onClick={() => saveUnitChanges(index)}
                                        disabled={isCompletedLead || !changedUnits[index] || Boolean(savingUnits[index])}
                                    >
                                        <SaveRoundedIcon fontSize='inherit' />
                                        <span>{savingUnits[index] ? 'Salvando...' : 'Salvar unidade'}</span>
                                    </button>
                                </>
                                )}
                    </div>

                    <div className='crm-lead-details-unit-flags'>
                        <Pill tone='is-neutral'>CC {unit?.codigo_do_cliente || '—'}</Pill>
                        {hasDocumentFieldValue(unit?.instalacao) && <Pill tone='is-neutral'>Instalação {unit.instalacao}</Pill>}
                        {activeFlags.map((flag) => <Pill key={flag.field} tone={flag.tone}>{flag.label}</Pill>)}
                        {changedUnits[index] && <Pill tone='is-warning'>Alterações sem salvar</Pill>}
                    </div>
                </header>

                {isExpanded && (
                    <div id={panelId} className='crm-lead-details-unit-body'>
                        {isEditing ? renderUnitEditGrid(unit, index) : renderUnitViewGrid(unit)}
                    </div>
                )}
            </article>
        );
    };

    const renderUploadBox = () => (
        <div className='crm-lead-details-upload-box'>
            <div className='crm-lead-details-upload-head'>
                <span className='crm-lead-details-upload-icon'><UploadFileRoundedIcon fontSize='inherit' /></span>
                <div className='crm-lead-details-upload-copy-group'>
                    <p className='crm-lead-details-upload-title'>Inserir unidade por PDF</p>
                    <p className='crm-lead-details-upload-copy'>
                        Envie a conta de energia em PDF. Os dados da unidade são lidos automaticamente.
                    </p>
                </div>
            </div>

            <div className='crm-lead-details-upload-actions'>
                <TextField
                    className='crm-lead-details-text-field crm-lead-details-upload-input'
                    type='file'
                    onChange={(e) => {
                        setUnitPdfFile(e.target.files?.[0] ?? null);
                        setShowManualUnitForm(false);
                    }}
                    fullWidth
                    inputProps={{ accept: 'application/pdf' }}
                    disabled={isCompletedLead}
                />
                <button
                    type='button'
                    className='crm-lead-details-button crm-lead-details-button-primary'
                    onClick={insertLeadUnits}
                    disabled={isCompletedLead || isInsertingUnit || isLoadingUnits}
                >
                    <AddRoundedIcon fontSize='inherit' />
                    <span>{isInsertingUnit ? 'Inserindo...' : 'Inserir unidade'}</span>
                </button>
            </div>

            {showManualUnitForm && (
                <div className='crm-lead-details-upload-manual'>
                    <p className='crm-lead-details-upload-manual-copy'>
                        Não foi possível ler o PDF. Preencha a unidade manualmente.
                    </p>
                    <div className='crm-lead-details-upload-manual-grid'>
                        {editableUnitFieldList.map((field) => (
                            <TextField
                                key={`manual-unit-${field}`}
                                className='crm-lead-details-text-field crm-lead-details-text-field-editable'
                                label={getUnitFieldLabel(field)}
                                value={manualUnitForm[field] ?? ''}
                                onChange={(e) => updateManualUnitField(field, e.target.value)}
                                disabled={isCompletedLead}
                                required
                                fullWidth
                            />
                        ))}

                        <FormControlLabel
                            sx={{ marginLeft: 0 }}
                            className='crm-lead-details-text-field'
                            control={(
                                <Switch
                                    checked={Boolean(manualUnitForm.alta_tensao)}
                                    onChange={(e) => updateManualUnitField('alta_tensao', e.target.checked)}
                                    disabled={isCompletedLead}
                                />
                            )}
                            label='Alta tensão'
                        />

                        {editableAddressFieldList.map((field) => (
                            field === 'estado'
                                ? (
                                    <FormControl key='manual-address-estado' className='crm-lead-details-select crm-dashboard-filter-control crm-dashboard-bordered-select' required>
                                        <InputLabel id='manual-address-estado-label'>Estado</InputLabel>
                                        <Select
                                            labelId='manual-address-estado-label'
                                            value={manualUnitForm.endereco?.estado ?? ''}
                                            onChange={(e) => updateManualUnitAddressField('estado', e.target.value)}
                                            disabled={isCompletedLead}
                                            IconComponent={KeyboardArrowDownRoundedIcon}
                                            MenuProps={selectMenuProps}
                                        >
                                            <MenuItem className='crm-dashboard-select-menu-item' value='PE'>PE</MenuItem>
                                            <MenuItem className='crm-dashboard-select-menu-item' value='RN'>RN</MenuItem>
                                        </Select>
                                    </FormControl>
                                    )
                                : (
                                    <TextField
                                        key={`manual-address-${field}`}
                                        className='crm-lead-details-text-field crm-lead-details-text-field-editable'
                                        label={getUnitFieldLabel(field)}
                                        value={manualUnitForm.endereco?.[field] ?? ''}
                                        onChange={(e) => updateManualUnitAddressField(field, e.target.value)}
                                        disabled={isCompletedLead}
                                        required={field !== 'complemento'}
                                        fullWidth
                                    />
                                    )
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const renderLeadUnitsState = () => {
        if (isLoadingUnits) {
            return (
                <div className='crm-client-state'>
                    <CircularProgress size={26} className='crm-client-state-spinner' />
                    <span className='crm-client-state-copy'>Carregando as unidades do lead...</span>
                </div>
            );
        }

        if (leadUnits.length === 0) {
            return (
                <div className='crm-client-state'>
                    <span className='crm-client-state-title'>Nenhuma unidade vinculada</span>
                    <span className='crm-client-state-copy'>
                        Insira a conta de energia em PDF para vincular a primeira unidade e liberar a geração do contrato.
                    </span>
                </div>
            );
        }

        return (
            <div className='crm-lead-details-units-list'>
                {leadUnits.map(renderLeadUnit)}
            </div>
        );
    };

    const currentPhase = FASE_OPTIONS.find((option) => matchesLeadOption(initialLeadData, 'fase', 'fase_id', option));
    const missingContractFields = DOCUMENT_REQUIRED_FIELDS.filter(({ field }) => !hasDocumentFieldValue(leadData[field]));

    const goToFirstMissingField = () => {
        setActiveTab('lead');
        const editableField = missingContractFields.find(({ field }) => {
            const config = LEAD_FIELD_CONFIGS.find((item) => item.field === field);
            return config && !getLeadFieldLockReason(config);
        });
        if (editableField) startLeadFieldEdit(editableField.field);
    };

    const goToUnitInsertion = () => {
        setActiveTab('unidades');
        setShowUnitUploadBox(true);
    };

    const buildNextStep = () => {
        if (hasChanges) {
            return {
                key: 'save',
                icon: SaveRoundedIcon,
                label: isSubmitting ? 'Salvando...' : 'Salvar alterações',
                reason: `${changedFieldCount} ${changedFieldCount === 1 ? 'campo alterado' : 'campos alterados'} sem salvar.`,
                onClick: saveChanges,
                disabled: isSubmitting
            };
        }

        if (isCompletedLead) {
            if (!hasClientRegistration) return null;
            if (canCreateAdditive) {
                return {
                    key: 'additive',
                    icon: NoteAddRoundedIcon,
                    label: 'Gerar aditivo',
                    reason: 'Cliente conquistado. Unidades novas entram por aditivo.',
                    onClick: handleCreateAdditive
                };
            }
            return {
                key: 'referral',
                icon: ContentCopyRoundedIcon,
                label: 'Copiar link de indicação',
                reason: 'Cliente ativo: um bom momento para pedir indicações.',
                onClick: handleCopyReferralLink
            };
        }

        if (isLostLeadStatus(initialLeadData.status)) return null;

        if (currentPhase?.id === CONTRACT_PHASE.id) {
            const daysWaiting = getDaysSince(initialLeadData.phase_four_date);
            const waitingText = daysWaiting === null ? '' : ` ${formatDaysAgo(daysWaiting)}`;

            return {
                key: 'signature',
                icon: WhatsAppIcon,
                label: 'Cobrar assinatura no WhatsApp',
                reason: hasValidPhone
                    ? `Contrato emitido${waitingText}, aguardando assinatura.`
                    : 'Telefone em formato inválido: corrija o cadastro para cobrar pelo WhatsApp.',
                href: buildWhatsAppUrl(leadData.telefone, buildSignatureReminderText(leadData.nome)),
                disabled: !hasValidPhone
            };
        }

        if (hasClientRegistration) return null;

        if (missingContractFields.length > 0) {
            return {
                key: 'complete',
                icon: EditRoundedIcon,
                label: 'Completar dados',
                reason: `Falta ${joinLabelsPtBr(missingContractFields.map(({ label }) => label))} para o contrato.`,
                onClick: goToFirstMissingField
            };
        }

        if (canViewLeadUnits && !isLoadingUnits && leadUnits.length === 0) {
            return {
                key: 'unit',
                icon: AddRoundedIcon,
                label: 'Inserir unidade',
                reason: 'O contrato precisa de ao menos uma unidade.',
                onClick: goToUnitInsertion
            };
        }

        if (currentPhase?.id === FASE_OPTIONS[0].id) {
            return {
                key: 'proposal',
                icon: RequestQuoteRoundedIcon,
                label: isGeneratingProposal ? 'Gerando proposta...' : 'Gerar proposta',
                reason: 'Lead em prospecção: envie a proposta.',
                onClick: handleGenerateProposal,
                disabled: !canGenerateProposal || isGeneratingProposal
            };
        }

        if (canCreateDocument) {
            return {
                key: 'contract',
                icon: NoteAddRoundedIcon,
                label: isCheckingSerasa ? 'Consultando Serasa...' : 'Gerar contrato',
                reason: 'Dados e unidades prontos. O Serasa é consultado ao clicar.',
                onClick: handleCreateDocumentClick,
                disabled: isCheckingSerasa
            };
        }

        return null;
    };

    const nextStep = buildNextStep();
    const isNextStep = (key) => nextStep?.key === key;

    const visibleTabs = CRM_CLIENT_TABS.filter((tab) => {
        if (CLIENT_ONLY_TAB_KEYS.has(tab.key)) return hasMatricula;
        if (tab.key === 'unidades') return canViewLeadUnits || hasMatricula;
        return true;
    });
    const currentTab = visibleTabs.some((tab) => tab.key === activeTab) ? activeTab : 'lead';

    const documentButton = isCompletedLead && hasClientRegistration
        ? (
            <>
                {!isNextStep('additive') && (
                    <button
                        type='button'
                        className='crm-lead-details-button crm-lead-details-button-secondary'
                        onClick={handleCreateAdditive}
                        disabled={!canCreateAdditive}
                    >
                        <NoteAddRoundedIcon fontSize='inherit' />
                        <span>Gerar aditivo</span>
                    </button>
                )}
                {!canCreateAdditive && <ActionHint>{additiveBlockReason}</ActionHint>}
                {canDistract && (
                    <button
                        type='button'
                        className='crm-lead-details-button crm-lead-details-button-danger'
                        onClick={openClientDistract}
                        disabled={isLoadingDistractUnits || isDistracting}
                    >
                        <CancelPresentationRoundedIcon fontSize='inherit' />
                        <span>Distratar cliente</span>
                    </button>
                )}
            </>
            )
        : (
            <>
                {!isNextStep('contract') && (
                    <button
                        type='button'
                        className='crm-lead-details-button crm-lead-details-button-secondary'
                        onClick={handleCreateDocumentClick}
                        disabled={isCompletedLead || !canCreateDocument || isCheckingSerasa}
                    >
                        <NoteAddRoundedIcon fontSize='inherit' />
                        <span>{isCheckingSerasa ? 'Consultando Serasa...' : 'Gerar contrato'}</span>
                    </button>
                )}
                {!canCreateDocument && <ActionHint>{documentBlockReason}</ActionHint>}
            </>
            );

    return (
        <div className='mainBox crm-lead-details-page'>
            <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />

            {openDistractDialog && (
                <ClientDistractDialog
                    clientName={clientContacts.cliente?.cliente || initialLeadData?.nome}
                    documentOptions={distractDocuments}
                    unitNames={distractUnitNames}
                    isLoadingUnits={isLoadingDistractUnits}
                    isBusy={isDistracting}
                    onClose={() => setOpenDistractDialog(false)}
                    onConfirm={confirmClientDistract}
                />
            )}

            <PopupDialog
                titulo='Descartar alterações'
                texto='Tem certeza que deseja descartar as alterações não salvas?'
                open={openDiscardDialog}
                handleClose={() => setOpenDiscardDialog(false)}
                funcao={confirmDiscardChanges}
            />

            <PopupDialog
                titulo='Enviar código de verificação'
                texto={`Enviar o código de verificação para ${contractEmail}?`}
                open={openEmailVerificationDialog}
                handleClose={() => setOpenEmailVerificationDialog(false)}
                funcao={sendEmailVerification}
            />

            <PopupDialog
                titulo='Gerar contrato'
                texto={`O vendedor da oportunidade é "${AI_AGENT_SELLER_LABEL}". Deseja continuar?`}
                open={openAiAgentContractDialog}
                handleClose={() => setOpenAiAgentContractDialog(false)}
                funcao={handleCreateDocument}
            />

            <div className='crm-lead-details-shell'>
                <header className='crm-lead-details-header'>
                    <button
                        type='button'
                        className='crm-lead-details-back'
                        onClick={navigateBackToLeads}
                        aria-label='Voltar para a lista de leads'
                        title='Voltar para a lista de leads'
                    >
                        <ArrowBackRoundedIcon fontSize='inherit' />
                    </button>

                    <div className='crm-lead-details-heading'>
                        <span className='crm-lead-details-kicker'>
                            Lead #{leadData.lead_id}
                            {hasMatricula && ` · Matrícula ${clienteId}`}
                        </span>
                        <h1 className='crm-lead-details-title'>{leadData.nome}</h1>
                        <div className='crm-lead-details-badges'>
                            <span className={`ui-tone-pill ${getLeadStatusToneClass(leadData.status)}`}>
                                {leadData.status || 'Sem status'}
                            </span>
                            <span className={`ui-tone-pill ${getLeadPhaseToneClass(leadData.fase)}`}>
                                {leadData.fase || 'Sem fase'}
                            </span>
                            <span className='crm-lead-details-badges-divider' aria-hidden='true' />
                            <span className='crm-lead-details-meta'>
                                <PersonRoundedIcon fontSize='inherit' />
                                {leadData.vendedor || 'Sem vendedor'}
                            </span>
                            <span className='crm-lead-details-meta'>
                                <CampaignRoundedIcon fontSize='inherit' />
                                {leadData.origem || 'Origem não informada'}
                            </span>
                        </div>
                    </div>

                    <div className='crm-lead-details-contact-actions' aria-label='Contato com o lead'>
                        <HeaderContactAction
                            label='Abrir conversa no WhatsApp'
                            icon={WhatsAppIcon}
                            href={buildWhatsAppUrl(leadData.telefone)}
                            disabledReason={phoneDisabledReason}
                            external
                        />
                        <HeaderContactAction
                            label='Ligar'
                            icon={PhoneRoundedIcon}
                            href={buildPhoneUrl(leadData.telefone)}
                            disabledReason={phoneDisabledReason}
                        />
                        <HeaderContactAction
                            label='Copiar telefone'
                            icon={ContentCopyRoundedIcon}
                            onClick={() => copyText(maskPhone(leadData.telefone), 'Telefone copiado.')}
                            disabledReason={hasDocumentFieldValue(leadData.telefone) ? '' : 'Lead sem telefone.'}
                        />
                        <HeaderContactAction
                            label='Enviar e-mail'
                            icon={MailOutlineRoundedIcon}
                            href={`mailto:${contractEmail}`}
                            disabledReason={canSendEmailVerification ? '' : 'Lead sem e-mail.'}
                        />
                        <span className='crm-lead-details-contact-divider' aria-hidden='true' />
                        <HeaderContactAction
                            label='Copiar link do lead'
                            icon={LinkRoundedIcon}
                            onClick={() => copyText(buildLeadUrl(leadData.lead_id), 'Link do lead copiado.')}
                        />
                    </div>
                </header>

                <div className='crm-lead-details-layout'>
                    <main className='crm-lead-details-main'>
                        <nav className='crm-lead-details-tabs' aria-label='Seções do lead'>
                            {visibleTabs.map((tab) => {
                                const TabIcon = TAB_ICONS[tab.key];

                                return (
                                    <button
                                        key={tab.key}
                                        type='button'
                                        className={`crm-lead-details-tab${currentTab === tab.key ? ' is-active' : ''}`}
                                        aria-current={currentTab === tab.key}
                                        onClick={() => setActiveTab(tab.key)}
                                    >
                                        {TabIcon && <TabIcon fontSize='inherit' />}
                                        {tab.label}
                                        {tab.key === 'unidades' && canViewLeadUnits && !isLoadingUnits && leadUnits.length > 0 && (
                                            <span className='crm-lead-details-tab-count'>{leadUnits.length}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </nav>

                        {currentTab === 'lead' && (
                            <div className='crm-lead-details-card'>
                                <div className='crm-client-stack'>
                                    {isCompletedLead && (
                                        <div className='crm-lead-details-notice'>
                                            <LockRoundedIcon fontSize='inherit' />
                                            <span>
                                                Lead conquistado: os dados ficam bloqueados para edição.
                                                {' O dia de vencimento continua liberado.'}
                                                {canOverrideSellerLock && ' Como supervisão, você ainda pode trocar o vendedor.'}
                                            </span>
                                        </div>
                                    )}

                                    <div className='crm-client-tiles'>
                                        <SummaryTile
                                            icon={EventRoundedIcon}
                                            label='Cadastrado em'
                                            value={formatDateTimeTextPtBr(leadData.cadastro) || '—'}
                                        />
                                        <SummaryTile
                                            icon={HomeWorkRoundedIcon}
                                            label='Unidades vinculadas'
                                            value={isLoadingUnits ? '…' : leadUnits.length}
                                            hint={isLoadingUnits
                                                ? 'carregando'
                                                : leadUnits.length === 1 ? 'unidade do lead' : 'unidades do lead'}
                                        />
                                        <SummaryTile
                                            icon={BadgeRoundedIcon}
                                            label='Matrícula'
                                            value={hasMatricula ? clienteId : '—'}
                                            hint={hasMatricula ? 'cliente cadastrado' : 'lead ainda não é cliente'}
                                        />
                                    </div>

                                    <p className='crm-lead-details-read-hint'>
                                        <EditRoundedIcon fontSize='inherit' />
                                        Clique em um campo para editar. Enter confirma, Esc desfaz.
                                    </p>

                                    <LeadSection icon={PersonRoundedIcon} title='Identificação'>
                                        <div className='crm-lead-details-grid is-read-mode'>
                                            {identityFields.map(renderLeadField)}
                                        </div>
                                    </LeadSection>

                                    <LeadSection icon={HandshakeRoundedIcon} title='Comercial'>
                                        <div className='crm-lead-details-grid is-read-mode'>
                                            {selectFields.map(renderLeadField)}
                                            {commercialTextFields.map(renderLeadField)}
                                            {renderLeadField(dueDayField)}
                                        </div>
                                    </LeadSection>

                                    <LeadSection icon={LocalOfferRoundedIcon} title='Indicação e cupom'>
                                        <div className='crm-lead-details-grid is-read-mode'>
                                            {referralFields.map(renderLeadField)}
                                        </div>
                                    </LeadSection>

                                    <LeadSection icon={NotesRoundedIcon} title='Observações'>
                                        <div className='crm-lead-details-grid is-read-mode'>
                                            {renderLeadField(obsField)}
                                        </div>
                                    </LeadSection>
                                </div>
                            </div>
                        )}

                        {currentTab === 'unidades' && (
                            <>
                                {canViewLeadUnits && (
                                    <div className='crm-lead-details-card'>
                                        <div className='crm-lead-details-card-header'>
                                            <div className='crm-lead-details-section-title-row'>
                                                <h2 className='crm-lead-details-section-title'>
                                                    Unidades do lead
                                                    {!isLoadingUnits && <span className='crm-client-block-count'>{leadUnits.length}</span>}
                                                </h2>
                                                <button
                                                    type='button'
                                                    className='crm-lead-details-button crm-lead-details-button-secondary is-compact'
                                                    onClick={() => setShowUnitUploadBox((prev) => !prev)}
                                                    disabled={isCompletedLead}
                                                    aria-expanded={showUnitUploadBox}
                                                >
                                                    {showUnitUploadBox ? <RemoveRoundedIcon fontSize='inherit' /> : <AddRoundedIcon fontSize='inherit' />}
                                                    <span>{showUnitUploadBox ? 'Ocultar inserção' : 'Inserir unidade'}</span>
                                                </button>
                                            </div>
                                            <p className='crm-lead-details-section-copy'>
                                                Unidades vinculadas a este lead. São elas que entram no contrato.
                                            </p>
                                        </div>

                                        {showUnitUploadBox && renderUploadBox()}

                                        {renderLeadUnitsState()}
                                    </div>
                                )}

                                {hasMatricula && (
                                    <div className='crm-lead-details-card'>
                                        <div className='crm-lead-details-card-header'>
                                            <h2 className='crm-lead-details-section-title'>Unidades do cliente</h2>
                                            <p className='crm-lead-details-section-copy'>
                                                Endereço real, troca de titularidade e distrato, vindos do cadastro do cliente.
                                            </p>
                                        </div>

                                        <ClientPanelState
                                            isLoading={isClientTabLoading('unidades')}
                                            hasMatricula={hasMatricula}
                                            isEmpty={clientUnits.length === 0}
                                            emptyLabel='Este cliente não possui unidades cadastradas.'
                                        />

                                        {!isClientTabLoading('unidades') && clientUnits.length > 0 && (
                                            <ClientUnitsPanel
                                                units={clientUnits}
                                                canDeactivate={canDeactivateUnit}
                                                onDeactivate={deactivateUnit}
                                                busyUnitId={busyUnitId}
                                            />
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        {currentTab === 'contato' && (
                            <div className='crm-lead-details-card'>
                                <div className='crm-lead-details-card-header'>
                                    <div className='crm-lead-details-section-title-row'>
                                        <h2 className='crm-lead-details-section-title'>Contato</h2>
                                        <div className='crm-lead-details-section-actions'>
                                            {canUpdateContact && (
                                                <button
                                                    type='button'
                                                    className='crm-lead-details-button crm-lead-details-button-secondary is-compact'
                                                    onClick={() => setShowContactForm((prev) => !prev)}
                                                    aria-expanded={showContactForm}
                                                >
                                                    {showContactForm ? <RemoveRoundedIcon fontSize='inherit' /> : <EditRoundedIcon fontSize='inherit' />}
                                                    <span>{showContactForm ? 'Fechar alteração' : 'Alterar contato'}</span>
                                                </button>
                                            )}
                                            {canUpdatePortalLogin && (
                                                <button
                                                    type='button'
                                                    className='crm-lead-details-button crm-lead-details-button-secondary is-compact'
                                                    onClick={() => setShowPortalForm((prev) => !prev)}
                                                    aria-expanded={showPortalForm}
                                                >
                                                    {showPortalForm ? <RemoveRoundedIcon fontSize='inherit' /> : <LockResetRoundedIcon fontSize='inherit' />}
                                                    <span>{showPortalForm ? 'Fechar dados do portal' : 'Alterar dados do portal'}</span>
                                                </button>
                                            )}
                                            {canUpdatePortalLogin && (
                                                <button
                                                    type='button'
                                                    className='crm-lead-details-button crm-lead-details-button-secondary is-compact'
                                                    onClick={copyPortalUserToFakeUser}
                                                    disabled={isCopyingPortalUser}
                                                >
                                                    <ContentCopyRoundedIcon fontSize='inherit' />
                                                    <span>{isCopyingPortalUser ? 'Copiando...' : 'Copiar usuário portal'}</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {canUpdateContact && showContactForm && (
                                    <ClientContactForm
                                        unitOptions={contactUnitOptions}
                                        isSaving={isSavingContact}
                                        onCancel={() => setShowContactForm(false)}
                                        onSave={saveClientContact}
                                    />
                                )}

                                {canUpdatePortalLogin && showPortalForm && (
                                    <ClientPortalLoginForm
                                        currentEmail={clientContacts.cliente?.email_portal}
                                        isSavingPassword={isSavingPortalPassword}
                                        isSavingEmail={isSavingPortalEmail}
                                        onCancel={() => setShowPortalForm(false)}
                                        onChangePassword={changePortalPassword}
                                        onChangeEmail={changePortalEmail}
                                    />
                                )}

                                <ClientPanelState
                                    isLoading={isClientTabLoading('contato')}
                                    hasMatricula={hasMatricula}
                                    isEmpty={!clientContacts.cliente && clientContacts.contatos.length === 0}
                                    emptyLabel='Nenhum contato encontrado para este cliente.'
                                />

                                {hasMatricula && !isClientTabLoading('contato') &&
                                    (clientContacts.cliente || clientContacts.contatos.length > 0) && (
                                    <ClientContactsPanel contacts={clientContacts} />
                                )}
                            </div>
                        )}

                        {currentTab === 'protocolos' && (
                            <div className='crm-lead-details-card'>
                                <div className='crm-lead-details-card-header'>
                                    <h2 className='crm-lead-details-section-title'>Protocolos</h2>
                                </div>

                                <ClientPanelState
                                    isLoading={isClientTabLoading('protocolos')}
                                    hasMatricula={hasMatricula}
                                    isEmpty={clientProtocols.length === 0}
                                    emptyLabel='Nenhum protocolo aberto para este cliente.'
                                />

                                {hasMatricula && !isClientTabLoading('protocolos') && clientProtocols.length > 0 && (
                                    <ClientProtocolsPanel protocols={clientProtocols} />
                                )}
                            </div>
                        )}

                        {currentTab === 'financeiro' && (
                            <div className='crm-lead-details-card'>
                                <div className='crm-lead-details-card-header'>
                                    <h2 className='crm-lead-details-section-title'>Financeiro</h2>
                                </div>

                                <ClientPanelState
                                    isLoading={isClientTabLoading('financeiro')}
                                    hasMatricula={hasMatricula}
                                    isEmpty={!hasFinanceRows}
                                    emptyLabel='Nenhum lançamento financeiro para este cliente.'
                                />

                                {hasMatricula && !isClientTabLoading('financeiro') && hasFinanceRows && (
                                    <ClientFinancePanel
                                        finance={clientFinance}
                                        onReceivablesPageChange={setFinancePage}
                                        onCancelReceivable={cancelClientReceivable}
                                        onExtendReceivable={extendClientReceivable}
                                        busyLancamento={busyLancamento}
                                    />
                                )}
                            </div>
                        )}
                    </main>

                    <aside className='crm-lead-details-side-panel' aria-label='Ações do lead'>
                        <div className='crm-lead-details-side-summary'>
                            <span>Lead #{leadData.lead_id}</span>
                            <strong>{leadData.nome}</strong>
                            <small>{leadData.telefone}</small>
                        </div>

                        <div className={`crm-lead-details-save-state${hasChanges ? ' has-changes' : ''}`} role='status'>
                            <span className='crm-lead-details-save-dot' aria-hidden='true' />
                            <span>
                                {hasChanges
                                    ? `${changedFieldCount} ${changedFieldCount === 1 ? 'campo alterado' : 'campos alterados'} sem salvar`
                                    : 'Nenhuma alteração pendente'}
                            </span>
                        </div>

                        {nextStep && (
                            <div className='crm-lead-details-next-step'>
                                <span className='crm-lead-details-action-label'>Próximo passo</span>
                                {nextStep.href && !nextStep.disabled
                                    ? (
                                        <a
                                            className='crm-lead-details-button crm-lead-details-button-primary'
                                            href={nextStep.href}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            <nextStep.icon fontSize='inherit' />
                                            <span>{nextStep.label}</span>
                                        </a>
                                        )
                                    : (
                                        <button
                                            type='button'
                                            className='crm-lead-details-button crm-lead-details-button-primary'
                                            onClick={nextStep.onClick}
                                            disabled={Boolean(nextStep.disabled)}
                                        >
                                            <nextStep.icon fontSize='inherit' />
                                            <span>{nextStep.label}</span>
                                        </button>
                                        )}
                                <p className='crm-lead-details-next-step-reason'>{nextStep.reason}</p>
                            </div>
                        )}

                        {hasChanges && (
                            <div className='crm-lead-details-action-group'>
                                <span className='crm-lead-details-action-label'>Alterações</span>
                                {!isNextStep('save') && (
                                    <button
                                        type='button'
                                        className='crm-lead-details-button crm-lead-details-button-secondary'
                                        onClick={saveChanges}
                                        disabled={isSubmitting}
                                    >
                                        <SaveRoundedIcon fontSize='inherit' />
                                        <span>{isSubmitting ? 'Salvando...' : 'Salvar alterações'}</span>
                                    </button>
                                )}
                                <button
                                    type='button'
                                    className='crm-lead-details-button crm-lead-details-button-warning'
                                    onClick={handleDiscardChanges}
                                    disabled={isSubmitting}
                                >
                                    <UndoRoundedIcon fontSize='inherit' />
                                    <span>Descartar alterações</span>
                                </button>
                            </div>
                        )}

                        <div className='crm-lead-details-action-group'>
                            <span className='crm-lead-details-action-label'>Documentos</span>
                            {documentButton}
                            {!isNextStep('proposal') && (
                                <button
                                    type='button'
                                    className='crm-lead-details-button crm-lead-details-button-secondary'
                                    onClick={handleGenerateProposal}
                                    disabled={!canGenerateProposal || isGeneratingProposal}
                                >
                                    <RequestQuoteRoundedIcon fontSize='inherit' />
                                    <span>{isGeneratingProposal ? 'Gerando proposta...' : 'Gerar proposta'}</span>
                                </button>
                            )}
                            {!canGenerateProposal && <ActionHint>{proposalBlockReason}</ActionHint>}
                            <button
                                type='button'
                                className='crm-lead-details-button crm-lead-details-button-secondary'
                                onClick={() => setOpenEmailVerificationDialog(true)}
                                disabled={!canSendEmailVerification || isSendingEmailVerification}
                            >
                                <MarkEmailReadRoundedIcon fontSize='inherit' />
                                <span>{isSendingEmailVerification ? 'Enviando...' : 'Enviar código de verificação'}</span>
                            </button>
                            {!canSendEmailVerification && <ActionHint>O lead não tem e-mail cadastrado.</ActionHint>}
                        </div>

                        {hasClientRegistration && !isNextStep('referral') && (
                            <div className='crm-lead-details-action-group'>
                                <span className='crm-lead-details-action-label'>Cliente</span>
                                <button
                                    type='button'
                                    className='crm-lead-details-button crm-lead-details-button-secondary'
                                    onClick={handleCopyReferralLink}
                                >
                                    <ContentCopyRoundedIcon fontSize='inherit' />
                                    <span>Copiar link de indicação</span>
                                </button>
                            </div>
                        )}

                        <div className='crm-lead-details-action-group is-back'>
                            <button
                                type='button'
                                className='crm-lead-details-button crm-lead-details-button-ghost'
                                onClick={navigateBackToLeads}
                            >
                                <ArrowBackRoundedIcon fontSize='inherit' />
                                <span>Voltar para a lista</span>
                            </button>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default CrmLeadDetails;
